/**
 * File upload validation utilities and shared constants.
 *
 * Centralizes limits and validation logic to avoid duplication between
 * AttachModal and inline drag/paste in ChatInputSimple.
 */

import {
  getAllAcceptedMimeTypes,
} from '../../../utils/fileTypeUtils';

export const MAX_FILES = 20;
export const MAX_FILE_SIZE_BYTES = 100 * 1024 * 1024; // 100MB

export const ACCEPTED_FILE_TYPE_LABEL = 'PDF, DOCX, PPTX, CSV, XLS, XLSX, JPEG, JPG, PNG';

export const MAX_FILE_SIZE_LABEL = '100MB';

/**
 * Mantine Dropzone `accept` value used across upload surfaces.
 * We return a string[] of accepted mime types for parity with existing usage.
 */
export function getDropzoneAccept(): string[] {
  return getAllAcceptedMimeTypes();
}

export interface LimitsValidationResult {
  valid: boolean;
  message?: string;
}

/**
 * Validate incoming files against max count and size limits.
 * @param files Files being added in the operation
 * @param existingCount Count of files already associated with the conversation
 */
export function validateAgainstLimits(files: File[], existingCount: number): LimitsValidationResult {
  if (!Array.isArray(files) || files.length === 0) {
    return { valid: false, message: 'No files provided.' };
  }

  if (files.length > MAX_FILES) {
    return {
      valid: false,
      message: `Cannot upload ${files.length} files at once. Maximum allowed: ${MAX_FILES} files per upload.`
    };
  }

  const total = existingCount + files.length;
  if (total > MAX_FILES) {
    return {
      valid: false,
      message: `Cannot upload ${files.length} file(s). This conversation already has ${existingCount} file(s). Maximum allowed: ${MAX_FILES} files per conversation.`
    };
  }

  for (const file of files) {
    if (file.size > MAX_FILE_SIZE_BYTES) {
      const sizeMb = Math.round(file.size / 1024 / 1024);
      return {
        valid: false,
        message: `File "${file.name}" is too large (${sizeMb}MB). Maximum size is ${MAX_FILE_SIZE_LABEL} per file.`
      };
    }
  }

  return { valid: true };
}

export interface TypeValidationResult {
  valid: boolean;
  message?: string;
}

/**
 * Validate supported file types.
 *
 * New file upload allows mixed supported types and PPTX.
 * Legacy upload preserves prod behaviour: no PPTX, one file category per upload,
 * and uploaded files must match the existing conversation file category.
 */
export function validateFileTypes(files: File[], existingFilenames: string[] = []): TypeValidationResult {
  if (!files.length) return { valid: false, message: 'No files provided.' };

  const acceptedMimeTypes = getDropzoneAccept();

  for (const file of files) {
    if (!acceptedMimeTypes.includes(file.type)) {
      return {
        valid: false,
        message: `File "${file.name}" has an unsupported file type. Please upload ${ACCEPTED_FILE_TYPE_LABEL} files.`
      };
    }
  }

  return { valid: true };
}
